'use client';
import React, { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import SectionHeader from '@/components/SectionHeader';
import { Download, Star, TrendingUp, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useRealtimeContent } from '@/hooks/useRealtimeContent';

interface PopularMovie {
  id: string;
  title: string;
  slug: string;
  year: number;
  imdb_rating: number;
  genre: string;
  poster_url: string;
  download_count: number;
}

export default function PopularDownloadsSection() {
  const [popular, setPopular] = useState<PopularMovie[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchPopular = useCallback(async () => {
    const { data, error } = await supabase
      .from('movies')
      .select('id, title, slug, year, imdb_rating, genre, poster_url, download_count')
      .order('download_count', { ascending: false })
      .limit(5);
    if (!error && data) setPopular(data as PopularMovie[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPopular();
  }, [fetchPopular]);

  useRealtimeContent({
    tables: ['movies'],
    onAnyChange: () => fetchPopular(),
  });

  return (
    <section>
      <SectionHeader
        title="Popular Downloads"
        subtitle="Most downloaded this month"
        viewAllHref="/movies-page"
        accentColor="cyan"
      />
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-primary" />
        </div>
      ) : popular.length === 0 ? (
        <div className="glass-card border border-border rounded-2xl p-10 text-center">
          <Download size={32} className="text-primary mx-auto mb-3 opacity-50" />
          <p className="text-sm text-muted-foreground font-body">No downloads yet. Check back soon!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {popular.map((movie, i) => (
            <Link key={`popular-${movie.id}`} href={`/movie-detail-page?slug=${movie.slug}`} className="block group">
              <div className="glass-card glass-card-hover rounded-xl p-4 flex items-center gap-4">
                {/* Rank */}
                <div className="flex-shrink-0 w-8 text-center">
                  <span className={`text-xl font-extrabold font-mono-data ${i < 3 ? 'neon-text-cyan' : 'text-muted-foreground'}`}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                {/* Poster */}
                <div className="flex-shrink-0 w-12 h-16 rounded-lg overflow-hidden">
                  <AppImage
                    src={movie.poster_url || '/assets/images/no_image.png'}
                    alt={`${movie.title} poster thumbnail`}
                    width={48}
                    height={64}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                    {movie.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground font-body">{movie.year}</span>
                    <span className="genre-badge">{movie.genre}</span>
                    <div className="flex items-center gap-0.5">
                      <Star size={10} className="rating-star fill-yellow-400" />
                      <span className="text-xs font-mono-data text-foreground">{movie.imdb_rating}</span>
                    </div>
                  </div>
                </div>
                {/* Download count */}
                <div className="flex-shrink-0 flex items-center gap-1.5 text-right">
                  <Download size={14} className="text-primary" />
                  <span className="font-mono-data font-bold text-sm text-primary">
                    {movie.download_count >= 1000
                      ? `${(movie.download_count / 1000).toFixed(1)}K`
                      : movie.download_count}
                  </span>
                </div>
                {/* Trend arrow */}
                {i < 3 && (
                  <div className="flex-shrink-0">
                    <TrendingUp size={16} className="text-green-400" />
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}